<template>
  <div class="iceberg-minute">
    <div class="header">
      <h2>分钟级冰山订单检测</h2>
      <div class="controls">
        <input
          v-model="stockCode"
          type="text"
          placeholder="股票代码 (如: 002602)"
          @keyup.enter="handleDetect"
          class="stock-input"
        />
        <el-date-picker
          v-model="startDate"
          type="date"
          placeholder="开始日期"
          class="date-picker"
          value-format="yyyy-MM-dd"
        />
        <el-date-picker
          v-model="endDate"
          type="date"
          placeholder="结束日期"
          class="date-picker"
          value-format="yyyy-MM-dd"
        />
        <button @click="handleDetect" :disabled="loading" class="detect-btn">
          {{ loading ? '检测中...' : '检测冰山订单' }}
        </button>
      </div>
    </div>

    <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

    <!-- 检测结果统计 -->
    <div v-if="detections.length > 0" class="results-section">
      <h3>检测结果 ({{ detections.length }}个疑似冰山订单)</h3>

      <!-- 检测类型统计 -->
      <div class="detection-summary">
        <div class="summary-item" v-for="(count, type) in detectionSummary" :key="type">
          <span class="summary-label">{{ getDetectionTypeLabel(type) }}</span>
          <span class="summary-count">{{ count }}</span>
        </div>
      </div>

      <!-- 检测结果表格 -->
      <div class="detection-table">
        <table>
          <thead>
            <tr>
              <th>检测类型</th>
              <th>描述</th>
              <th>时间</th>
              <th>持续时长</th>
              <th>主力净流入</th>
              <th>大单净流入</th>
              <th>超大单净流入</th>
              <th>价格变化</th>
              <th>置信度</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(det, index) in detections" :key="index">
              <td v-html="getDetectionTypeBadge(det.detection_type)"></td>
              <td>{{ det.description }}</td>
              <td>{{ getTimeDisplay(det) }}</td>
              <td>{{ det.duration_minutes ? det.duration_minutes + '分钟' : '-' }}</td>
              <td :class="det.total_main_inflow >= 0 ? 'positive' : 'negative'">
                {{ formatNumber(det.total_main_inflow || det.main_net_inflow) }}
              </td>
              <td :class="det.total_large_inflow >= 0 ? 'positive' : 'negative'">
                {{ formatNumber(det.total_large_inflow) }}
              </td>
              <td :class="(det.total_super_inflow || det.super_volume) >= 0 ? 'positive' : 'negative'">
                {{ formatNumber(det.total_super_inflow || det.super_volume) }}
              </td>
              <td>{{ det.price_change_pct !== undefined ? det.price_change_pct + '%' : '-' }}</td>
              <td :class="getConfidenceClass(det.confidence)">{{ det.confidence }}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 资金流向图表 -->
    <div v-if="chartData.length > 0" class="charts-section">
      <h3>资金流向分析</h3>
      <div class="chart-container">
        <div ref="flowChart" class="chart"></div>
      </div>
    </div>

    <div v-if="chartData.length > 0 && detections.length === 0" class="no-detection">
      <p>未检测到明显的冰山订单特征</p>
      <p class="hint">提示：冰山订单通常表现为大单持续流入但价格稳定等特征</p>
    </div>

    <!-- 使用说明 -->
    <div class="usage-guide">
      <h4>检测算法说明</h4>
      <ul>
        <li><strong>大单持续流入价格稳定</strong>: 大单资金持续流入但价格波动较小，疑似隐藏大单</li>
        <li><strong>超大单突然出现</strong>: 超大单突然出现后消失，疑似探路单</li>
        <li><strong>主力资金流向背离</strong>: 主力大量流入但价格上涨不大或下跌，疑似冰山订单</li>
        <li><strong>连续大单流入</strong>: 连续多分钟出现大单净流入，疑似机构持续建仓</li>
        <li><strong>大单突然中断</strong>: 大单流入突然中断，疑似冰山订单完成或撤销</li>
      </ul>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import axios from 'axios'

export default {
  name: 'IcebergMinuteView',
  data() {
    const today = new Date()
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(today.getDate() - 30)

    return {
      stockCode: '002602',
      startDate: this.formatDate(thirtyDaysAgo),
      endDate: this.formatDate(today),
      loading: false,
      errorMessage: '',
      detections: [],
      chartData: [],
      chart: null
    }
  },
  computed: {
    detectionSummary() {
      const summary = {}
      this.detections.forEach(det => {
        const type = det.detection_type
        summary[type] = (summary[type] || 0) + 1
      })
      return summary
    }
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose()
    }
  },
  methods: {
    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    async handleDetect() {
      if (!this.stockCode || !this.startDate || !this.endDate) {
        this.errorMessage = '请填写完整的查询参数'
        return
      }

      this.loading = true
      this.errorMessage = ''
      this.detections = []
      this.chartData = []

      if (this.chart) {
        this.chart.dispose()
        this.chart = null
      }

      try {
        const params = {
          code: this.stockCode,
          start_date: this.startDate,
          end_date: this.endDate
        }

        let response
        try {
          response = await axios.get('/flow/iceberg-detect/minute', { params })
        } catch (error) {
          console.warn('代理请求失败，尝试直接访问:', error)
          response = await axios.get(`http://localhost:7350/flow/iceberg-detect/minute?${new URLSearchParams(params)}`)
        }

        if (response.data.status === 'success') {
          this.detections = response.data.data || []
          this.chartData = response.data.chart_data || []

          if (this.chartData.length > 0) {
            this.$nextTick(() => {
              this.renderChart()
            })
          }
        } else {
          this.errorMessage = response.data.error || '检测失败'
        }
      } catch (error) {
        this.errorMessage = '请求失败: ' + (error.response?.data?.error || error.message || '网络错误')
      } finally {
        this.loading = false
      }
    },

    renderChart() {
      if (!this.$refs.flowChart) return

      this.chart = echarts.init(this.$refs.flowChart)

      const times = this.chartData.map(d => d.time)
      const mainNetInflow = this.chartData.map(d => d.main_net_inflow)
      const largeNetInflow = this.chartData.map(d => d.large_net_inflow)
      const superNetInflow = this.chartData.map(d => d.super_net_inflow)
      const mediumNetInflow = this.chartData.map(d => d.medium_net_inflow)
      const smallNetInflow = this.chartData.map(d => d.small_net_initem_inflow || d.small_net_inflow)

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'cross' }
        },
        legend: {
          data: ['主力净流入', '超大单净流入', '大单净流入', '中单净流入', '小单净流入'],
          top: 10,
          textStyle: { color: '#e2e8f0' },
          backgroundColor: 'rgba(15, 23, 42, 0.9)',
          borderColor: 'rgba(99, 102, 241, 0.3)',
          borderWidth: 1,
          borderRadius: 8,
          padding: [12, 20]
        },
        grid: {
          top: 50,
          right: 80,
          bottom: 60,
          left: 80
        },
        xAxis: {
          type: 'category',
          data: times,
          axisLabel: {
            color: '#94a3b8',
            rotate: 45
          },
          axisLine: { lineStyle: { color: 'rgba(99, 102, 241, 0.3)' } }
        },
        yAxis: {
          type: 'value',
          name: '净流入(元)',
          nameTextStyle: { color: '#94a3b8' },
          axisLabel: {
            color: '#94a3b8',
            formatter: (value) => {
              if (value >= 100000000) return (value / 100000000).toFixed(1) + '亿'
              if (value >= 10000) return (value / 10000).toFixed(0) + '万'
              return value
            }
          },
          axisLine: { lineStyle: { color: 'rgba(99, 102, 241, 0.3)' } },
          splitLine: { lineStyle: { color: 'rgba(99, 102, 241, 0.1)' } }
        },
        dataZoom: [
          {
            type: 'inside',
            start: 0,
            end: 100
          },
          {
            type: 'slider',
            start: 0,
            end: 100,
            height: 20,
            bottom: 10,
            borderColor: 'rgba(99, 102, 241, 0.3)',
            fillerColor: 'rgba(99, 102, 241, 0.2)',
            handleStyle: { color: '#6366f1' },
            textStyle: { color: '#94a3b8' }
          }
        ],
        series: [
          {
            name: '主力净流入',
            type: 'bar',
            data: mainNetInflow,
            itemStyle: {
              color: (params) => params.value >= 0 ? '#ef4444' : '#22c55e'
            }
          },
          {
            name: '超大单净流入',
            type: 'line',
            data: superNetInflow,
            smooth: true,
            lineStyle: { color: '#d32f2f', width: 2 },
            itemStyle: { color: '#d32f2f' }
          },
          {
            name: '大单净流入',
            type: 'line',
            data: largeNetInflow,
            smooth: true,
            lineStyle: { color: '#f57c00', width: 2 },
            itemStyle: { color: '#f57c00' }
          },
          {
            name: '中单净流入',
            type: 'line',
            data: mediumNetInflow,
            smooth: true,
            lineStyle: { color: '#1976d2', width: 1 },
            itemStyle: { color: '#1976d2' }
          },
          {
            name: '小单净流入',
            type: 'line',
            data: smallNetInflow,
            smooth: true,
            lineStyle: { color: '#388e3c', width: 1 },
            itemStyle: { color: '#388e3c' }
          }
        ]
      }

      this.chart.setOption(option)
    },

    formatNumber(value) {
      if (value === undefined || value === null) return '-'
      const num = Number(value)
      if (Math.abs(num) >= 100000000) return (num / 100000000).toFixed(2) + '亿'
      if (Math.abs(num) >= 10000) return (num / 10000).toFixed(2) + '万'
      return num.toFixed(2)
    },

    getTimeDisplay(det) {
      if (det.start_time && det.end_time) {
        return `${det.start_time} - ${det.end_time}`
      }
      if (det.time) return det.time
      if (det.interrupt_time) return det.interrupt_time
      return '-'
    },

    getDetectionTypeLabel(type) {
      const labels = {
        'large_volume_stable_price': '大单持续流入价格稳定',
        'sudden_super_order': '超大单突然出现',
        'flow_divergence': '主力资金流向背离',
        'continuous_large_orders': '连续大单流入',
        'large_order_interruption': '大单突然中断'
      }
      return labels[type] || type
    },

    getDetectionTypeBadge(type) {
      const badges = {
        'large_volume_stable_price': '<span class="badge badge-primary">大单持续流入价格稳定</span>',
        'sudden_super_order': '<span class="badge badge-warning">超大单突然出现</span>',
        'flow_divergence': '<span class="badge badge-danger">主力资金流向背离</span>',
        'continuous_large_orders': '<span class="badge badge-info">连续大单流入</span>',
        'large_order_interruption': '<span class="badge badge-secondary">大单突然中断</span>'
      }
      return badges[type] || type
    },

    getConfidenceClass(confidence) {
      if (confidence >= 70) return 'high'
      if (confidence >= 50) return 'medium'
      return 'low'
    }
  }
}
</script>

<style scoped>
.iceberg-minute {
  padding: 20px;
  width: 100%;
  background: transparent;
  color: #e2e8f0;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
}

.header h2 {
  margin: 0;
  color: #e2e8f0;
  font-weight: 600;
}

.controls {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.stock-input {
  padding: 8px 12px;
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 4px;
  font-size: 14px;
  width: 150px;
  background: rgba(30, 41, 59, 0.8);
  color: #e2e8f0;
}

.date-picker {
  width: 160px;
}

.date-picker :deep(.el-input__inner) {
  background: rgba(30, 41, 59, 0.8);
  border-color: rgba(99, 102, 241, 0.3);
  color: #e2e8f0;
}

.date-picker :deep(.el-input__icon) {
  color: #94a3b8;
}

.detect-btn {
  padding: 8px 20px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  transition: all 0.3s ease;
}

.detect-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #818cf8 0%, #a78bfa 100%);
  box-shadow: 0 6px 16px rgba(99, 102, 241, 0.4);
}

.detect-btn:disabled {
  background: #475569;
  cursor: not-allowed;
  box-shadow: none;
}

.error-message {
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #fca5a5;
  padding: 10px 15px;
  border-radius: 6px;
  margin-bottom: 20px;
}

.results-section {
  margin-bottom: 30px;
}

.results-section h3 {
  color: #e2e8f0;
  margin-bottom: 15px;
}

.detection-summary {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.summary-item {
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 120px;
}

.summary-label {
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 4px;
}

.summary-count {
  font-size: 24px;
  font-weight: 700;
  color: #a5b4fc;
}

.detection-table {
  overflow-x: auto;
  background: rgba(30, 41, 59, 0.6);
  border-radius: 8px;
  border: 1px solid rgba(99, 102, 241, 0.2);
}

.detection-table table {
  width: 100%;
  border-collapse: collapse;
}

.detection-table th,
.detection-table td {
  padding: 12px;
  text-align: center;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
}

.detection-table th {
  background: rgba(15, 23, 42, 0.8);
  font-weight: 600;
  color: #e2e8f0;
  position: sticky;
  top: 0;
}

.detection-table tbody tr:hover {
  background: rgba(99, 102, 241, 0.1);
}

.detection-table td {
  color: #cbd5e1;
  font-size: 13px;
}

.detection-table td.positive {
  color: #ef4444;
}

.detection-table td.negative {
  color: #22c55e;
}

.detection-table td.high {
  color: #ef4444;
  font-weight: 600;
}

.detection-table td.medium {
  color: #fbbf24;
  font-weight: 600;
}

.detection-table td.low {
  color: #22c55e;
}

.badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.badge-primary {
  background: rgba(59, 130, 246, 0.2);
  color: #60a5fa;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

.badge-warning {
  background: rgba(251, 191, 36, 0.2);
  color: #fcd34d;
  border: 1px solid rgba(251, 191, 36, 0.3);
}

.badge-danger {
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.badge-info {
  background: rgba(34, 211, 238, 0.2);
  color: #22d3ee;
  border: 1px solid rgba(34, 211, 238, 0.3);
}

.badge-secondary {
  background: rgba(148, 163, 184, 0.2);
  color: #cbd5e1;
  border: 1px solid rgba(148, 163, 184, 0.3);
}

.charts-section {
  margin-bottom: 30px;
}

.charts-section h3 {
  color: #e2e8f0;
  margin-bottom: 15px;
}

.chart-container {
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  padding: 20px;
}

.chart {
  width: 100%;
  height: 450px;
}

.no-detection {
  text-align: center;
  padding: 40px;
  color: #94a3b8;
  background: rgba(30, 41, 59, 0.6);
  border-radius: 8px;
  border: 1px solid rgba(99, 102, 241, 0.2);
}

.no-detection p {
  margin: 10px 0;
}

.no-detection .hint {
  font-size: 14px;
  color: #64748b;
}

.usage-guide {
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  padding: 16px 20px;
}

.usage-guide h4 {
  margin: 0 0 12px 0;
  color: #e2e8f0;
  font-size: 15px;
}

.usage-guide ul {
  margin: 0;
  padding-left: 20px;
  color: #94a3b8;
  line-height: 1.8;
}

.usage-guide li {
  margin-bottom: 8px;
}

.usage-guide strong {
  color: #a5b4fc;
}
</style>
